import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_ProfessionalAiHeadshots = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/professional-ai-headshots',
			name: 'Professional Photo AI — AI Headshot Generator | Lift',
			description:
				'Create professional headshots with AI in minutes. No watermark. Upload selfies, choose styles, and get studio-quality business headshots instantly. Try now!',
			url: 'https://liftapp.ai/professional-ai-headshots',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/professional-ai-headshots#product' },
				{ '@id': 'https://liftapp.ai/professional-ai-headshots#howto' },
				{ '@id': 'https://liftapp.ai/professional-ai-headshots#benefits' },
				{ '@id': 'https://liftapp.ai/professional-ai-headshots#faq' },
				{ '@id': 'https://liftapp.ai/professional-ai-headshots#why-lift' },
				{ '@id': 'https://liftapp.ai/professional-ai-headshots#explore-tools' },
				{ '@id': 'https://liftapp.ai/professional-ai-headshots#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/professional-ai-headshots#product',
			name: 'AI Professional Headshot Generator',
			description:
				'Generate professional business headshots with AI in minutes. Get studio-quality professional photos from selfies — AI headshot generator, no watermarks.',
			url: 'https://liftapp.ai/professional-ai-headshots',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/professional-ai-headshots#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift generates professional business headshots from your selfies automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/professional-ai-headshots#benefits',
			name: 'Who can benefit from AI Headshot Generator?',
			description:
				"Lift's AI Headshot Generator is useful for professionals, job seekers, creators, and businesses who want professional headshots for LinkedIn, resumes, corporate websites, and personal branding.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Job Seekers & Career Switchers — Boost your resume and LinkedIn profile with confidence-inspiring professional headshots. Stand out to recruiters with polished, high-quality images that make strong first impressions.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Freelancers & Solopreneurs — Build your personal brand with professional headshots without expensive photo shoots. Get multiple styles for landing pages, proposals, and social profiles in one session.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Remote Teams — Create uniform, professional company photos for distributed teams instantly. No scheduling conflicts or studio logistics — just consistent, high-quality headshots for everyone.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Personal Brand Builders — Refresh your online presence with eye-catching professional images that strengthen your brand. Perfect for content creators, coaches, and influencers who need versatile, polished visuals.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Elevate academic profiles and professional portfolios with affordable, high-quality headshots. Ideal for conference bios, research profiles, and university staff directories without studio sessions.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI professional photo use cases',
				description:
					'Example of how Lift AI Headshot Generator creates professional headshots for various use cases.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/professional-ai-headshots#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Headshot Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Headshot Generator is a premium online tool that instantly creates professional business headshots from your selfies with AI-powered precision — ideal for LinkedIn profiles, corporate websites, or personal branding.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Headshot Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload a few selfies to Lift's AI headshot generator tool. Our AI automatically generates professional headshots in multiple styles and backgrounds in minutes — no photo shoot or editing experience needed. Download up to 20 professional headshots.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Headshot Generator uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking cutouts without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/professional-ai-headshots#why-lift',
			name: "Why Lift's AI Headshot Generator stands out",
			description:
				"Lift's AI Headshot Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Make professional photo online',
				description:
					"Comparison of Lift's AI Headshot Generator benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/professional-ai-headshots#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/professional-ai-headshots#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Professional AI Headshots',
					item: 'https://liftapp.ai/professional-ai-headshots',
				},
			],
		},
	],
};
